import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      {sent ? (
        <div data-testid="forgot-sent">
          <div className="h-11 w-11 rounded-full bg-orange-100 flex items-center justify-center mb-4">
            <MailCheck className="text-orange-600" size={22} />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Check your email</h2>
          <p className="text-slate-500 text-sm mt-2 leading-relaxed">
            If an account exists for <span className="font-medium text-slate-700">{email}</span>, we've sent a
            password reset link. It expires in 60 minutes.
          </p>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 mt-6"
            data-testid="back-to-login-link">
            <ArrowLeft size={15} /> Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <h2 className="font-display text-2xl font-bold text-slate-900">Reset your password</h2>
          <p className="text-slate-500 text-sm mt-1 mb-7">Enter your email and we'll send you a reset link.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="mt-1.5 h-11" data-testid="forgot-email-input" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
              className="w-full h-11 text-white font-medium" style={{ background: "var(--cp-primary)" }}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Send reset link"}
            </Button>
          </form>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 mt-6"
            data-testid="back-to-login-link">
            <ArrowLeft size={15} /> Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
}
